import './ManagerContractMobile.css';
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from './Components/Sidebar/Sidebar';
import DashboardNavbar from './Components/Navbar/DashboardNavbar';
import { Card, Spinner } from 'react-bootstrap';
import { FaFaucet, FaTint, FaDollarSign, FaPercentage, FaPiggyBank, FaChartBar, FaTable, FaArrowUp, FaArrowDown } from 'react-icons/fa';
import { BarChart, Bar, LineChart, Line, Area, ComposedChart, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://character-ai-app-635021ffeebd.herokuapp.com';

// ============ Helpers ============

function formatMonth(dateStr) {
  if (!dateStr) return '';
  const [year, month] = dateStr.split('-');
  const date = new Date(parseInt(year), parseInt(month) - 1);
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

function formatMonthShort(dateStr) {
  if (!dateStr) return '';
  const [year, month] = dateStr.split('-');
  const date = new Date(parseInt(year), parseInt(month) - 1);
  return date.toLocaleDateString('en-US', { month: 'short' });
}

function formatMonthYear(dateStr) {
  // "2026-01" -> "Jan 2026"
  if (!dateStr) return '';
  const [year, month] = dateStr.split('-');
  const date = new Date(parseInt(year), parseInt(month) - 1);
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

function formatDollars(amount) {
  if (amount == null || isNaN(amount)) return '$0';
  const abs = Math.abs(amount);
  const formatted = abs >= 1000
    ? '$' + abs.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
    : '$' + abs.toFixed(2);
  return amount < 0 ? '-' + formatted : formatted;
}

function formatM3(val) {
  if (val == null || isNaN(val)) return '0 m³';
  return Math.abs(val).toLocaleString('en-US', { maximumFractionDigits: 1 }) + ' m³';
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

// ============ Empty / Error / No Data States ============

const NoContractState = () => (
  <div style={{ width: '90%', margin: '2rem auto 0', backgroundColor: '#f8f9fa' }}>
    <Card className="shadow-lg border-0 rounded-lg">
      <Card.Body className="text-center p-5">
        <div style={{ fontSize: '3rem', color: '#224982', marginBottom: '1.5rem' }}>
          <FaFaucet />
        </div>
        <Card.Title style={{ fontSize: '2rem', marginBottom: '1.5rem', color: '#224982', fontWeight: '600' }}>
          Water Savings
        </Card.Title>
        <Card.Text style={{ color: '#596272', marginBottom: '2rem', fontSize: '1.1rem', lineHeight: '1.6', maxWidth: '800px', margin: '0 auto 2rem' }}>
          Reduce water consumption and save on utility costs with Hudson's comprehensive water savings solutions.
          Get detailed monitoring, leak detection, and efficiency tracking to optimize your building's water usage
          and maintain sustainable operations.
        </Card.Text>
        <div style={{ fontSize: '1.15rem', color: '#224982', fontWeight: 500 }}>
          Email <a href="mailto:service@hudsonhvac.ca">service@hudsonhvac.ca</a> for a free proposal
        </div>
      </Card.Body>
    </Card>
  </div>
);

const LoadingState = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
    <Spinner animation="border" role="status" style={{ color: '#2c59a2' }}>
      <span className="visually-hidden">Loading...</span>
    </Spinner>
  </div>
);

const ErrorState = ({ message }) => (
  <div style={{ width: '90%', margin: '2rem auto 0' }}>
    <Card className="border-0 rounded-lg" style={{ backgroundColor: '#fef2f2' }}>
      <Card.Body className="text-center p-5">
        <div style={{ fontSize: '2rem', color: '#dc2626', marginBottom: '1rem' }}>⚠️</div>
        <h4 style={{ color: '#dc2626', marginBottom: '0.5rem' }}>Error Loading Data</h4>
        <p style={{ color: '#6b7280' }}>{message || 'Something went wrong. Please try again later.'}</p>
      </Card.Body>
    </Card>
  </div>
);

const NoDataState = () => (
  <div style={{ width: '90%', margin: '2rem auto 0' }}>
    <Card className="border-0 rounded-lg shadow-sm">
      <Card.Body className="text-center p-5">
        <div style={{ fontSize: '2rem', color: '#9ca3af', marginBottom: '1rem' }}>
          <FaTint />
        </div>
        <h4 style={{ color: '#374151' }}>No Comparison Data Available Yet</h4>
        <p style={{ color: '#6b7280' }}>Data will appear here once the Hudson MEV Valve has been installed and collecting readings.</p>
      </Card.Body>
    </Card>
  </div>
);

// ============ Custom Blue Header with valve date top-right ============

function WaterSavingsHeader({ valveInstallDate }) {
  const userStr = localStorage.getItem('managerUser');
  let firstName = "";
  if (userStr) {
    try {
      const user = JSON.parse(userStr);
      firstName = user.first_name || "";
    } catch (err) { /* ignore */ }
  }

  return (
    <div className="header-wrapper">
      <div className="header-inner">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem' }}>
          <h5 className="welcome-text" style={{ margin: 0 }}>
            <span className="welcome-highlight">Hi {firstName},</span> welcome back!
          </h5>
          {valveInstallDate && (
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              backgroundColor: '#dbeafe',
              color: '#1e40af',
              borderRadius: '20px',
              padding: '0.4rem 1rem',
              fontSize: '0.85rem',
              fontWeight: '500'
            }}>
              <FaFaucet style={{ fontSize: '0.8rem' }} />
              Hudson MEV Valve Installed: {formatDate(valveInstallDate)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============ Stat Card ============

function StatCard({ icon, iconBg, iconColor, label, value, valueColor, subtitle }) {
  return (
    <div style={{
      flex: '1 1 0',
      minWidth: '200px',
      backgroundColor: '#fff',
      borderRadius: '12px',
      padding: '1.5rem 1.75rem',
      boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)',
      display: 'flex',
      flexDirection: 'column',
      gap: '0.85rem'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <div style={{
          width: '42px',
          height: '42px',
          borderRadius: '10px',
          backgroundColor: iconBg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: iconColor,
          fontSize: '1.15rem',
          flexShrink: 0
        }}>
          {icon}
        </div>
        <span style={{ fontSize: '0.95rem', color: '#6b7280', fontWeight: '500', lineHeight: '1.2' }}>{label}</span>
      </div>
      <div style={{ fontSize: '2.4rem', fontWeight: '700', color: valueColor || '#1f2937', lineHeight: '1' }}>
        {value}
      </div>
      {subtitle && (
        <div style={{ fontSize: '0.9rem', color: '#9ca3af' }}>{subtitle}</div>
      )}
    </div>
  );
}

// ============ Custom Chart Tooltip ============

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  const current = payload.find(p => p.dataKey === 'currentM3');
  const baseline = payload.find(p => p.dataKey === 'baselineM3');
  // Access full data entry from payload
  const entry = payload[0]?.payload || {};
  const diffM3 = entry.diffM3;
  const savings = entry.savingsDollars;
  const ratePerM3 = entry.ratePerM3;
  const hasDiff = diffM3 != null && baseline?.value != null && current?.value != null;
  const isPositive = diffM3 > 0;

  return (
    <div style={{
      backgroundColor: '#fff',
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      padding: '0.85rem 1.1rem',
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      fontSize: '0.85rem',
      minWidth: '220px'
    }}>
      <div style={{ fontWeight: '600', marginBottom: '0.6rem', color: '#374151', fontSize: '0.9rem' }}>{entry.fullMonth || label}</div>
      {baseline && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.3rem' }}>
          <div style={{ width: 10, height: 10, borderRadius: 2, backgroundColor: '#cbd5e1' }} />
          <span style={{ color: '#6b7280' }}>Baseline: {baseline.value?.toLocaleString('en-US', { maximumFractionDigits: 1 })} m³</span>
        </div>
      )}
      {current && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.3rem' }}>
          <div style={{ width: 10, height: 10, borderRadius: 2, backgroundColor: '#2c59a2' }} />
          <span style={{ color: '#6b7280' }}>Current: {current.value?.toLocaleString('en-US', { maximumFractionDigits: 1 })} m³</span>
        </div>
      )}
      {hasDiff && (
        <>
          <div style={{ borderTop: '1px solid #e5e7eb', margin: '0.5rem 0', paddingTop: '0.5rem' }}>
            <div style={{ color: '#6b7280', marginBottom: '0.25rem' }}>
              Difference: <strong style={{ color: isPositive ? '#059669' : '#dc2626' }}>
                {isPositive ? '-' : '+'}{Math.abs(diffM3).toLocaleString('en-US', { maximumFractionDigits: 1 })} m³
              </strong>
            </div>
            <div style={{ color: '#6b7280', fontSize: '0.8rem' }}>
              {Math.abs(diffM3).toLocaleString('en-US', { maximumFractionDigits: 1 })} m³ × ${ratePerM3 ? ratePerM3.toFixed(2) : '4.93'}/m³
            </div>
            {isPositive && (
              <div style={{ 
                fontWeight: '700', 
                fontSize: '1rem', 
                color: '#059669',
                marginTop: '0.3rem'
              }}>
                Saved: {formatDollars(Math.abs(savings || 0))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ============ Custom Legend ============

function CustomLegend() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', gap: '2rem', paddingTop: '1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
        <div style={{ width: 12, height: 12, borderRadius: 2, backgroundColor: '#cbd5e1' }} />
        <span style={{ color: '#6b7280', fontSize: '0.85rem' }}>Baseline (Last Year)</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
        <div style={{ width: 12, height: 12, borderRadius: 2, backgroundColor: '#2c59a2' }} />
        <span style={{ color: '#6b7280', fontSize: '0.85rem' }}>Current (With Hudson MEV Valve)</span>
      </div>
    </div>
  );
}

// ============ Main Component ============

export default function WaterSavings() {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(true);
  const [buildingName, setBuildingName] = useState('');
  const [buildingManagerName, setBuildingManagerName] = useState();
  const [buildingPhotoUrl, setBuildingPhotoUrl] = useState('');
  const [hasWaterSavingsContract, setHasWaterSavingsContract] = useState(null);
  const [hasProjectContract, setHasProjectContract] = useState(false);
  const [hasValveAuditContract, setHasValveAuditContract] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [waterData, setWaterData] = useState(null);
  const [waterLoading, setWaterLoading] = useState(false);
  const [waterError, setWaterError] = useState(null);
  const [dateFilter, setDateFilter] = useState(null); // will be set dynamically once data loads

  const token = localStorage.getItem('managerToken');
  const userStr = localStorage.getItem('managerUser') || "";
  let buildingId = null;
  try {
    const parsedUser = JSON.parse(userStr);
    buildingId = parsedUser?.buildingId;
  } catch (e) {
    console.error("Could not parse managerUser from localStorage");
  }

  useEffect(() => {
    const fetchBuildingData = async () => {
      if (!buildingId) { setIsLoading(false); return; }
      try {
        const response = await fetch(`${API_BASE_URL}/api/getBuildingById`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ buildingId })
        });
        if (!response.ok) throw new Error('Failed to fetch building data');
        const data = await response.json();
        setBuildingName(data.name || "Unknown Building");
        setBuildingManagerName(Object.prototype.hasOwnProperty.call(data, 'manager') ? data.manager || '' : data.managerProfile?.manager || '');
        setBuildingPhotoUrl(data.photo_url || "");
        const contracts = data.contracts || [];
        const hasContract = contracts.includes("water_savings");
        setHasWaterSavingsContract(hasContract);
        setHasProjectContract(contracts.includes('project'));
        setHasValveAuditContract(contracts.includes('valve-audit'));

        if (hasContract) {
          setWaterLoading(true);
          try {
            const wsResp = await fetch(`${API_BASE_URL}/api/water-savings`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
              body: JSON.stringify({ buildingId })
            });
            if (!wsResp.ok) throw new Error(`Water savings data error: ${wsResp.status}`);
            setWaterData(await wsResp.json());
          } catch (wsErr) {
            console.error("[WaterSavings] Error fetching water data:", wsErr);
            setWaterError(wsErr.message);
          } finally { setWaterLoading(false); }
        }
      } catch (err) {
        console.error("fetchBuildingData error:", err);
        setHasWaterSavingsContract(false);
      } finally { setIsLoading(false); }
    };
    fetchBuildingData();
  }, [token, buildingId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Set default date filter based on available data
  useEffect(() => {
    if (!waterData || dateFilter !== null) return;
    const comparisons = waterData.comparisons || [];
    if (comparisons.length >= 12) {
      setDateFilter('last12');
    } else if (comparisons.length >= 3) {
      setDateFilter('last3');
    } else if (comparisons.length >= 1) {
      setDateFilter('last1');
    } else {
      // No completed months — show daily view
      setDateFilter('currentMonth');
    }
  }, [waterData, dateFilter]);

  // Derived data — backend only returns completed months
  const allComparisons = waterData?.comparisons || [];
  const lastCompletedMonth = allComparisons.length > 0 ? allComparisons[allComparisons.length - 1] : null;

  // Lifetime totals — use backend's pre-calculated totals (not affected by date filter)
  const lifetimeTotals = waterData?.totals || { totalSavedDollars: 0, totalSavedM3: 0, totalBaselineM3: 0, totalCurrentM3: 0, yourShare: 0 };

  // Lifetime average percentage — calculated as total saved / total baseline (not average of monthly %)
  // This avoids outlier months with tiny baselines distorting the average
  const lifetimeAvgPercent = useMemo(() => {
    const totalBaseline = lifetimeTotals.totalBaselineM3 || 0;
    const totalSaved = lifetimeTotals.totalSavedM3 || 0;
    if (totalBaseline === 0) return 0;
    return (totalSaved / totalBaseline) * 100;
  }, [lifetimeTotals]);

  // Apply date filter
  const filteredComparisons = useMemo(() => {
    if (dateFilter === 'all') return allComparisons;
    const count = dateFilter === 'last1' ? 1 : dateFilter === 'last3' ? 3 : 12;
    return allComparisons.slice(-count);
  }, [allComparisons, dateFilter]);

  // All filtered comparisons are completed (backend handles ongoing separately)
  const completedComparisons = filteredComparisons;

  // Recalculate totals from filtered months
  const completedTotals = useMemo(() => {
    if (completedComparisons.length === 0) return { totalBaselineM3: 0, totalCurrentM3: 0, totalSavedM3: 0, totalSavedDollars: 0, yourShare: 0 };
    const totalBaselineM3 = completedComparisons.reduce((sum, c) => sum + (c.baselineM3 || 0), 0);
    const totalCurrentM3 = completedComparisons.reduce((sum, c) => sum + (c.currentM3 || 0), 0);
    const totalSavedM3 = completedComparisons.reduce((sum, c) => sum + (c.diffM3 || 0), 0);
    const totalSavedDollars = completedComparisons.reduce((sum, c) => sum + (c.savingsDollars || 0), 0);
    return { totalBaselineM3, totalCurrentM3, totalSavedM3, totalSavedDollars, yourShare: totalSavedDollars / 2 };
  }, [completedComparisons]);

  // Average percentage saved across filtered months
  const avgPercentSaved = useMemo(() => {
    if (completedComparisons.length === 0) return 0;
    const totalPercent = completedComparisons.reduce((sum, c) => sum + (c.diffPercent || 0), 0);
    return totalPercent / completedComparisons.length;
  }, [completedComparisons]);

  // Chart data
  const chartData = useMemo(() => {
    return filteredComparisons.map((c) => {
      const [bYear, bMonth] = (c.baselineMonth || '').split('-');
      const [cYear, cMonth] = (c.currentMonth || '').split('-');
      const bDate = bYear && bMonth ? new Date(parseInt(bYear), parseInt(bMonth) - 1) : null;
      const cDate = cYear && cMonth ? new Date(parseInt(cYear), parseInt(cMonth) - 1) : null;
      const baselineLabel = bDate ? bDate.toLocaleDateString('en-US', { month: 'short' }) + " '" + bYear.slice(2) : '';
      const currentLabel = cDate ? cDate.toLocaleDateString('en-US', { month: 'short' }) + " '" + cYear.slice(2) : '';
      return {
        month: formatMonthShort(c.currentMonth),
        fullMonth: formatMonth(c.currentMonth),
        baselineLabel,
        currentLabel,
        baselineM3: c.baselineM3,
        currentM3: c.currentM3,
        diffM3: c.diffM3,
        diffPercent: c.diffPercent,
        savingsDollars: c.savingsDollars,
        ratePerM3: c.ratePerM3 || waterData?.waterRate || 4.93
      };
    });
  }, [filteredComparisons, waterData]);

  // Current month daily chart data — compare daily actual vs baseline daily average
  const dailyChartData = useMemo(() => {
    const currentMonthlyData = waterData?.currentMonthlyData || [];
    if (currentMonthlyData.length === 0) return [];

    // Parse date string manually to avoid timezone issues (e.g., "2026-02-01" -> month=2, year=2026)
    const firstDate = currentMonthlyData[0]?.date;
    if (!firstDate) return [];
    const [yearStr, monthStr] = firstDate.split('-');
    const currentYear = parseInt(yearStr);
    const currentMonthNum = parseInt(monthStr); // 1-indexed (Feb = 2)
    const daysInCurrentMonth = new Date(currentYear, currentMonthNum, 0).getDate();

    // Find the matching baseline month (match by month number)
    const baselineData = waterData?.baselineData || [];
    const matchingBaseline = baselineData.find(b => {
      const [, m] = b.month.split('-');
      return parseInt(m) === currentMonthNum;
    });

    // Calculate baseline daily average
    let baselineDailyAvg = 0;
    if (matchingBaseline) {
      const [bYear, bMonth] = matchingBaseline.month.split('-');
      const daysInBaselineMonth = new Date(parseInt(bYear), parseInt(bMonth), 0).getDate();
      baselineDailyAvg = matchingBaseline.m3 / daysInBaselineMonth;
    }

    // Build daily data points
    const monthPadded = String(currentMonthNum).padStart(2, '0');
    return Array.from({ length: daysInCurrentMonth }, (_, i) => {
      const dayNum = i + 1;
      const dayStr = String(dayNum).padStart(2, '0');
      const dateStr = `${currentYear}-${monthPadded}-${dayStr}`;
      const actualEntry = currentMonthlyData.find(d => d.date === dateStr);

      return {
        day: dayNum,
        label: `${new Date(currentYear, currentMonthNum - 1, dayNum).toLocaleDateString('en-US', { month: 'short' })} ${dayNum}`,
        actual: actualEntry ? actualEntry.m3 : null,
        baselineAvg: Math.round(baselineDailyAvg * 10) / 10
      };
    });
  }, [waterData]);

  const currentMonthLabel = useMemo(() => {
    const currentMonthlyData = waterData?.currentMonthlyData || [];
    if (currentMonthlyData.length === 0) return 'Current Month';
    const firstDate = currentMonthlyData[0]?.date;
    if (!firstDate) return 'Current Month';
    const [yearStr, monthStr] = firstDate.split('-');
    const date = new Date(parseInt(yearStr), parseInt(monthStr) - 1);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }, [waterData]);

  const baselineMonthName = useMemo(() => {
    const currentMonthlyData = waterData?.currentMonthlyData || [];
    if (currentMonthlyData.length === 0) return '';
    const firstDate = currentMonthlyData[0]?.date;
    if (!firstDate) return '';
    const [, monthStr] = firstDate.split('-');
    const currentMonthNum = parseInt(monthStr);
    const baselineData = waterData?.baselineData || [];
    const matching = baselineData.find(b => {
      const [, m] = b.month.split('-');
      return parseInt(m) === currentMonthNum;
    });
    return matching ? formatMonth(matching.month) : '';
  }, [waterData]);

  const handleNavigate = (key) => {
    switch (key) {
      case 'hvac': navigate('/'); break;
      case 'mech': navigate('/mechanical-room'); break;
      case 'dryerVents': navigate('/dryer-vents'); break;
      case 'stackCleaning': navigate('/stack-cleaning'); break;
      case 'waterSavings': navigate('/water-savings'); break;
      case 'reports': navigate('/reports'); break;
      case 'projects': navigate('/projects'); break;
      case 'valveAudit': navigate('/valve-audit'); break;
      default: break;
    }
  };

  const renderContent = () => {
    if (isLoading || waterLoading) return <LoadingState />;
    if (!hasWaterSavingsContract) return <NoContractState />;
    if (dateFilter === null) return <LoadingState />;
    if (waterError) return <ErrorState message={waterError} />;
    if (!waterData || allComparisons.length === 0) return <NoDataState />;

    // Pills always use lifetime / last month values (not affected by date filter)
    const savedLastMonth = lastCompletedMonth?.diffM3 || 0;
    const costSavedLastMonth = lastCompletedMonth?.savingsDollars || 0;
    const totalSaved = lifetimeTotals.totalSavedDollars;
    const lastMonthLabel = lastCompletedMonth ? formatMonthYear(lastCompletedMonth.currentMonth) : '';
    const isSavingPositive = savedLastMonth > 0;
    const isCostPositive = costSavedLastMonth > 0;
    const isAvgPositive = lifetimeAvgPercent > 0;

    return (
      <div className="water-savings-content" style={{ width: '90%', margin: '0 auto', paddingBottom: '3rem' }}>

        {/* ---- Section: Overview — Cumulative Chart + 4 Stat Pills ---- */}
        <div style={{ marginBottom: '2rem', marginTop: '2.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '1.25rem' }}>
            <FaTint style={{ color: '#2c59a2', fontSize: '1.25rem' }} />
            <h3 style={{ margin: 0, fontWeight: '700', color: '#1f2937', fontSize: '1.5rem' }}>Overview</h3>
          </div>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            {/* Left: Cumulative chart — 40% width */}
            {(() => {
              const currentMonthlyData = waterData?.currentMonthlyData || [];
              if (currentMonthlyData.length === 0) return null;
              const firstDate = currentMonthlyData[0]?.date;
              if (!firstDate) return null;
              const [yearStr, monthStr] = firstDate.split('-');
              const cYear = parseInt(yearStr);
              const cMonthNum = parseInt(monthStr);
              const daysInMonth = new Date(cYear, cMonthNum, 0).getDate();
              const monthName = new Date(cYear, cMonthNum - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
              const bData = waterData?.baselineData || [];
              const matchBase = bData.find(b => parseInt(b.month.split('-')[1]) === cMonthNum);
              const baseTotal = matchBase?.m3 || 0;
              const baseLabel = matchBase ? formatMonth(matchBase.month) : '';
              let cumActual = 0;
              const cumData = Array.from({ length: daysInMonth }, (_, i) => {
                const dayNum = i + 1;
                const ds = `${cYear}-${String(cMonthNum).padStart(2,'0')}-${String(dayNum).padStart(2,'0')}`;
                const ent = currentMonthlyData.find(d => d.date === ds);
                if (ent) cumActual += ent.m3;
                const bCum = (baseTotal / daysInMonth) * dayNum;
                const hasD = currentMonthlyData.some(d => d.date <= ds);
                return { day: dayNum, baseline: Math.round(bCum * 10) / 10, actual: hasD ? Math.round(cumActual * 10) / 10 : null, actualArea: hasD ? Math.round(cumActual * 10) / 10 : null };
              });
              const lastDay = Math.max(...currentMonthlyData.map(d => parseInt(d.date.split('-')[2])));
              const dispData = cumData.map(d => ({ ...d, actual: d.day <= lastDay ? d.actual : null, actualArea: d.day <= lastDay ? d.actualArea : null }));
              const savedSoFar = (baseTotal / daysInMonth) * lastDay - cumActual;
              const isSavedPositive = savedSoFar > 0;

              return (
                <div className="water-savings-summary-chart" style={{
                  flex: '2 1 320px',
                  backgroundColor: '#fff',
                  borderRadius: '12px',
                  padding: '1.25rem',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)',
                  minWidth: '320px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <h5 style={{ margin: 0, fontWeight: '700', color: '#1f2937', fontSize: '1.1rem', lineHeight: '1.3' }}>
                      {monthName}<br/>
                      <span style={{ fontWeight: '500', fontSize: '0.85rem', color: '#6b7280', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                        <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#dc2626', display: 'inline-block', boxShadow: '0 0 6px rgba(220, 38, 38, 0.5)' }} />
                        Live Daily Tracking
                      </span>
                    </h5>
                    {savedSoFar !== 0 && (
                      <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.3rem',
                        backgroundColor: isSavedPositive ? '#d1fae5' : '#fee2e2',
                        color: isSavedPositive ? '#059669' : '#dc2626',
                        borderRadius: '12px',
                        padding: '0.3rem 0.75rem',
                        fontSize: '0.82rem',
                        fontWeight: '600',
                        whiteSpace: 'nowrap'
                      }}>
                        {isSavedPositive ? <FaArrowDown style={{ fontSize: '0.65rem' }} /> : <FaArrowUp style={{ fontSize: '0.65rem' }} />}
                        {Math.abs(savedSoFar).toFixed(0)} m³ {isSavedPositive ? 'below' : 'above'} baseline
                      </div>
                    )}
                  </div>
                  <ResponsiveContainer width="100%" height={220}>
                    <ComposedChart data={dispData} margin={{ top: 5, right: 5, bottom: 0, left: -10 }}>
                      <defs>
                        <linearGradient id="actualGradientSmall" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#2c59a2" stopOpacity={0.2} />
                          <stop offset="95%" stopColor="#2c59a2" stopOpacity={0.05} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                      <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fill: '#9ca3af', fontSize: 10 }} interval={Math.floor(daysInMonth / 7)} />
                      <YAxis axisLine={false} tickLine={false} tick={{ fill: '#9ca3af', fontSize: 10 }} width={40} />
                      <Tooltip
                        content={({ active, payload, label }) => {
                          if (!active || !payload?.length) return null;
                          const act = payload.find(p => p.dataKey === 'actual');
                          const bas = payload.find(p => p.dataKey === 'baseline');
                          return (
                            <div style={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '0.5rem 0.75rem', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', fontSize: '0.8rem' }}>
                              <div style={{ fontWeight: '600', color: '#374151', marginBottom: '0.25rem' }}>Day {label}</div>
                              {bas && <div style={{ color: '#6b7280' }}>Baseline: {bas.value?.toFixed(1)} m³</div>}
                              {act?.value != null && <div style={{ color: '#2c59a2' }}>Actual: {act.value?.toFixed(1)} m³</div>}
                              {act?.value != null && bas?.value != null && (
                                <div style={{ fontWeight: '600', fontSize: '0.75rem', color: act.value < bas.value ? '#059669' : '#dc2626', marginTop: '0.2rem' }}>
                                  {act.value < bas.value ? `↓ ${(bas.value - act.value).toFixed(1)} m³ saved` : `↑ ${(act.value - bas.value).toFixed(1)} m³ extra`}
                                </div>
                              )}
                            </div>
                          );
                        }}
                      />
                      <Line dataKey="baseline" type="linear" stroke="#cbd5e1" strokeWidth={2} strokeDasharray="6 3" dot={false} connectNulls />
                      <Area dataKey="actualArea" type="monotone" fill="url(#actualGradientSmall)" stroke="none" connectNulls />
                      <Line dataKey="actual" type="monotone" stroke="#2c59a2" strokeWidth={2.5} dot={false} connectNulls />
                    </ComposedChart>
                  </ResponsiveContainer>
                  <div style={{ display: 'flex', justifyContent: 'center', gap: '1.5rem', marginTop: '0.25rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                      <div style={{ width: 14, height: 2, backgroundColor: '#cbd5e1' }} />
                      <span style={{ color: '#9ca3af', fontSize: '0.7rem' }}>Baseline ({baseLabel})</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                      <div style={{ width: 14, height: 2.5, backgroundColor: '#2c59a2', borderRadius: 1 }} />
                      <span style={{ color: '#9ca3af', fontSize: '0.7rem' }}>Actual</span>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Right: 4 Stat pills in 2x2 grid — 60% width */}
            <div className="water-savings-stats" style={{ flex: '3 1 480px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', minWidth: '350px' }}>
              <StatCard
                icon={<FaPercentage />}
                iconBg="#dbeafe"
                iconColor="#2563eb"
                label="Avg. Water Savings Since Installation"
                value={isAvgPositive ? lifetimeAvgPercent.toFixed(1) + '%' : '0%'}
                valueColor="#059669"
                subtitle={`Based on ${allComparisons.length} completed month${allComparisons.length !== 1 ? 's' : ''}`}
              />
              <StatCard
                icon={<FaDollarSign />}
                iconBg="#d1fae5"
                iconColor="#059669"
                label="Avg. Monthly Savings"
                value={totalSaved > 0 ? formatDollars(totalSaved / allComparisons.length) + ' / mo' : '$0 / mo'}
                valueColor="#1f2937"
                subtitle={`Over ${allComparisons.length} month${allComparisons.length !== 1 ? 's' : ''}`}
              />
              <StatCard
                icon={<FaTint />}
                iconBg="#ede9fe"
                iconColor="#7c3aed"
                label="Total Water Saved"
                value={lifetimeTotals.totalSavedM3 > 0 ? formatM3(lifetimeTotals.totalSavedM3) : '0 m³'}
                valueColor="#2c59a2"
                subtitle="Since installation"
              />
              <StatCard
                icon={<FaPiggyBank />}
                iconBg="#d1fae5"
                iconColor="#059669"
                label="Total Saved Since Installation"
                value={totalSaved > 0 ? formatDollars(totalSaved) : '$0'}
                valueColor="#059669"
                subtitle={`Based on ${allComparisons.length} month${allComparisons.length !== 1 ? 's' : ''}`}
              />
            </div>
          </div>
        </div>

        {/* ---- Historical Savings Header + Date Filter ---- */}
        {/* ---- Historical Savings Header + Date Filter ---- */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem', marginTop: '3rem', flexWrap: 'wrap', gap: '0.75rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <FaChartBar style={{ color: '#2c59a2', fontSize: '1.25rem' }} />
            <h3 style={{ margin: 0, fontWeight: '700', color: '#1f2937', fontSize: '1.5rem' }}>Historical Savings</h3>
          </div>
          <div style={{ display: 'flex', gap: '0.35rem', backgroundColor: '#f3f4f6', borderRadius: '10px', padding: '4px' }}>
            {[
              { key: 'all', label: 'All Time' },
              { key: 'last12', label: 'Last 12 Months' },
              { key: 'last3', label: 'Last 3 Months' },
              { key: 'last1', label: 'Last Month' },
              { key: 'currentMonth', label: 'Current Month' },
            ].map(opt => (
              <button
                key={opt.key}
                onClick={() => setDateFilter(opt.key)}
                style={{
                  padding: '0.55rem 1.1rem',
                  borderRadius: '8px',
                  border: 'none',
                  fontSize: '0.92rem',
                  fontWeight: dateFilter === opt.key ? '600' : '500',
                  backgroundColor: dateFilter === opt.key ? '#fff' : 'transparent',
                  color: dateFilter === opt.key ? '#1e40af' : '#6b7280',
                  boxShadow: dateFilter === opt.key ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  whiteSpace: 'nowrap'
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* ---- Section: Chart (Monthly or Daily depending on filter) ---- */}
        {dateFilter === 'currentMonth' ? (
          /* ---- Current Month Daily Chart ---- */
          <div style={{
            backgroundColor: '#fff',
            borderRadius: '12px',
            padding: '1.5rem',
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            marginBottom: '2rem',
            overflow: 'hidden'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '0.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <h5 style={{ margin: 0, fontWeight: '600', color: '#1f2937' }}>
                  Daily Water Usage — {currentMonthLabel}
                </h5>
                {baselineMonthName && (
                  <span style={{ fontSize: '0.8rem', color: '#9ca3af' }}>
                    (vs {baselineMonthName})
                  </span>
                )}
              </div>
            </div>
            {dailyChartData.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: '#9ca3af' }}>
                No daily data available for the current month yet.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={380}>
                <BarChart data={dailyChartData} barCategoryGap="15%" barGap={0} margin={{ top: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                  <XAxis
                    dataKey="day"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#6b7280', fontSize: 11, fontWeight: 500 }}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#9ca3af', fontSize: 12 }}
                    label={{ value: 'm³', position: 'insideLeft', offset: -5, style: { fill: '#9ca3af', fontSize: 12 } }}
                  />
                  <Tooltip
                    content={({ active, payload, label }) => {
                      if (!active || !payload?.length) return null;
                      const actual = payload.find(p => p.dataKey === 'actual');
                      const baseline = payload.find(p => p.dataKey === 'baselineAvg');
                      return (
                        <div style={{
                          backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px',
                          padding: '0.75rem 1rem', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', fontSize: '0.85rem'
                        }}>
                          <div style={{ fontWeight: '600', marginBottom: '0.4rem', color: '#374151' }}>Day {label}</div>
                          {actual && actual.value != null && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.2rem' }}>
                              <div style={{ width: 10, height: 10, borderRadius: 2, backgroundColor: '#2c59a2' }} />
                              <span style={{ color: '#6b7280' }}>Actual: {actual.value.toFixed(1)} m³</span>
                            </div>
                          )}
                          {baseline && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <div style={{ width: 10, height: 10, borderRadius: 2, backgroundColor: '#cbd5e1' }} />
                              <span style={{ color: '#6b7280' }}>Baseline avg: {baseline.value.toFixed(1)} m³</span>
                            </div>
                          )}
                          {actual?.value != null && baseline?.value != null && (
                            <div style={{ borderTop: '1px solid #eee', marginTop: '0.4rem', paddingTop: '0.4rem', fontWeight: '600',
                              color: actual.value < baseline.value ? '#059669' : '#dc2626', fontSize: '0.8rem' }}>
                              {actual.value < baseline.value
                                ? `↓ ${(baseline.value - actual.value).toFixed(1)} m³ less than baseline`
                                : `↑ ${(actual.value - baseline.value).toFixed(1)} m³ more than baseline`}
                            </div>
                          )}
                        </div>
                      );
                    }}
                    cursor={{ fill: 'rgba(0,0,0,0.03)' }}
                  />
                  <Legend
                    content={() => (
                      <div style={{ display: 'flex', justifyContent: 'center', gap: '2rem', paddingTop: '1rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                          <div style={{ width: 12, height: 12, borderRadius: 2, backgroundColor: '#cbd5e1' }} />
                          <span style={{ color: '#6b7280', fontSize: '0.85rem' }}>Baseline Daily Avg ({baselineMonthName})</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                          <div style={{ width: 12, height: 12, borderRadius: 2, backgroundColor: '#2c59a2' }} />
                          <span style={{ color: '#6b7280', fontSize: '0.85rem' }}>Actual Daily Usage</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                          <div style={{ width: 12, height: 3, borderRadius: 1, backgroundColor: '#f87171' }} />
                          <span style={{ color: '#6b7280', fontSize: '0.85rem' }}>Baseline Avg Line</span>
                        </div>
                      </div>
                    )}
                  />
                  <Bar dataKey="baselineAvg" fill="#cbd5e1" radius={[3, 3, 0, 0]} maxBarSize={20} />
                  <Bar dataKey="actual" fill="#2c59a2" radius={[3, 3, 0, 0]} maxBarSize={20} />
                  <Line
                    dataKey="baselineAvg"
                    type="monotone"
                    stroke="#f87171"
                    strokeWidth={2}
                    strokeDasharray="6 3"
                    dot={false}
                    connectNulls
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        ) : (
          /* ---- Monthly Bar Chart ---- */
          <div style={{
            backgroundColor: '#fff',
            borderRadius: '12px',
            padding: '1.5rem',
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            marginBottom: '2rem',
            overflow: 'hidden'
          }}>
            <div style={{ marginBottom: '1.25rem' }}>
              <h5 style={{ margin: 0, fontWeight: '600', color: '#1f2937' }}>Monthly Water Consumption Compared to Baseline (m³)</h5>
            </div>
          <div style={{ overflowX: 'auto', overflowY: 'hidden', maxWidth: '100%' }}>
            <div style={{ minWidth: chartData.length > 8 ? chartData.length * 100 : undefined, width: chartData.length > 8 ? undefined : '100%' }}>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={chartData} barCategoryGap="25%" barGap={0} margin={{ top: 25, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                  <XAxis
                    dataKey="month"
                    axisLine={false}
                    tickLine={false}
                    tick={false}
                    height={5}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#9ca3af', fontSize: 12 }}
                    tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}
                  />
                  <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.03)' }} />
                  <Legend content={<CustomLegend />} />
                  {/* Baseline bars first (left/gray), then current (right/blue) - touching with no gap */}
                  <Bar dataKey="baselineM3" name="baselineM3" radius={[4, 4, 0, 0]} maxBarSize={50}
                    label={({ x, y, width, index }) => {
                      const entry = chartData[index];
                      if (!entry) return null;
                      return (
                        <text x={x + width / 2} y={y - 6} textAnchor="middle" fill="#7c9ec9" fontSize={10} fontWeight={500}>
                          {entry.baselineLabel}
                        </text>
                      );
                    }}
                  >
                    {chartData.map((entry, idx) => (
                      <Cell key={`baseline-${idx}`} fill="#cbd5e1" />
                    ))}
                  </Bar>
                  <Bar dataKey="currentM3" name="currentM3" radius={[4, 4, 0, 0]} maxBarSize={50}
                    label={({ x, y, width, index }) => {
                      const entry = chartData[index];
                      if (!entry) return null;
                      return (
                        <text x={x + width / 2} y={y - 6} textAnchor="middle" fill="#1e3a6e" fontSize={10} fontWeight={600}>
                          {entry.currentLabel}
                        </text>
                      );
                    }}
                  >
                    {chartData.map((entry, idx) => (
                      <Cell key={`current-${idx}`} fill="#2c59a2" />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>

              {/* Savings percentage pills - aligned under bars */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: `repeat(${chartData.length}, 1fr)`,
                gap: '0.5rem',
                marginTop: '0.75rem',
                paddingLeft: '3.5rem',
                paddingRight: '0.5rem'
              }}>
            {chartData.map((entry, idx) => {
              const isPositive = entry.diffM3 > 0;
              return (
                <div key={idx} style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '0.25rem',
                  opacity: 1
                }}>
                  <span style={{ fontSize: '0.8rem', fontWeight: '500', color: '#6b7280', whiteSpace: 'nowrap' }}>
                    {entry.currentLabel}
                  </span>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.25rem',
                    backgroundColor: isPositive ? '#d1fae5' : '#fee2e2',
                    color: isPositive ? '#059669' : '#dc2626',
                    borderRadius: '12px',
                    padding: '0.3rem 0.6rem',
                    fontSize: '0.95rem',
                    fontWeight: '700',
                    whiteSpace: 'nowrap'
                  }}>
                    {isPositive ? <FaArrowDown style={{ fontSize: '0.7rem' }} /> : <FaArrowUp style={{ fontSize: '0.7rem' }} />}
                    {Math.abs(entry.diffPercent).toFixed(1)}%
                  </div>
                </div>
              );
            })}
              </div>
            </div>
          </div>
        </div>
        )}

        {/* ---- Section: Savings Breakdown Table (completed months only, hidden for current month view) ---- */}
        {dateFilter !== 'currentMonth' && (
        <div style={{
          backgroundColor: '#fff',
          borderRadius: '12px',
          padding: '1.5rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          marginBottom: '2rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.25rem' }}>
            <FaTable style={{ color: '#2c59a2', fontSize: '1rem' }} />
            <h5 style={{ margin: 0, fontWeight: '600', color: '#1f2937' }}>Savings Breakdown</h5>
            <span style={{ fontSize: '0.8rem', color: '#9ca3af', marginLeft: '0.5rem' }}>(completed months only)</span>
          </div>
          <div className="manager-table-scroll" role="region" aria-label="Water savings details, scroll horizontally for all columns" tabIndex={0} style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                  {['Month', 'Baseline (m³)', 'Current (m³)', 'Usage Reduction', 'Cost Saved', "Hudson's Fee"].map((h, i) => (
                    <th key={i} style={{
                      padding: '0.75rem 1rem',
                      textAlign: i === 0 ? 'left' : 'right',
                      color: '#374151',
                      fontWeight: '600',
                      whiteSpace: 'nowrap',
                      fontSize: '0.85rem'
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[...completedComparisons].reverse().map((c, idx) => {
                  const isPositive = c.diffM3 > 0;
                  const color = isPositive ? '#059669' : '#dc2626';
                  const halfSavings = c.savingsDollars / 2;
                  const ourShare = halfSavings > 0 ? halfSavings : 0;
                  return (
                    <tr key={idx} style={{
                      borderBottom: '1px solid #f3f4f6',
                      transition: 'background-color 0.15s'
                    }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f9fafb'}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = ''}
                    >
                      <td style={{ padding: '0.85rem 1rem', fontWeight: '500', color: '#1f2937' }}>
                        {formatMonth(c.currentMonth)}
                      </td>
                      <td style={{ padding: '0.85rem 1rem', textAlign: 'right', color: '#6b7280' }}>
                        {c.baselineM3.toLocaleString('en-US', { maximumFractionDigits: 1 })}
                      </td>
                      <td style={{ padding: '0.85rem 1rem', textAlign: 'right', color: '#6b7280' }}>
                        {c.currentM3.toLocaleString('en-US', { maximumFractionDigits: 1 })}
                      </td>
                      <td style={{ padding: '0.85rem 1rem', textAlign: 'right' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.5rem' }}>
                          <span style={{ fontWeight: '600', color }}>
                            {isPositive ? '+' : ''}{c.diffM3.toLocaleString('en-US', { maximumFractionDigits: 1 })} m³
                          </span>
                          <span style={{
                            backgroundColor: isPositive ? '#d1fae5' : '#fee2e2',
                            color,
                            borderRadius: '10px',
                            padding: '0.15rem 0.5rem',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            whiteSpace: 'nowrap'
                          }}>
                            {isPositive ? '↓' : '↑'} {Math.abs(c.diffPercent).toFixed(1)}%
                          </span>
                        </div>
                      </td>
                      <td style={{ padding: '0.85rem 1rem', textAlign: 'right', fontWeight: '600', color: c.savingsDollars > 0 ? '#059669' : '#6b7280' }}>
                        {formatDollars(Math.max(0, c.savingsDollars))}
                      </td>
                      <td style={{ padding: '0.85rem 1rem', textAlign: 'right' }}>
                        <span style={{
                          backgroundColor: ourShare > 0 ? '#d1fae5' : '#f3f4f6',
                          color: ourShare > 0 ? '#059669' : '#6b7280',
                          borderRadius: '6px',
                          padding: '0.25rem 0.65rem',
                          fontWeight: '600',
                          fontSize: '0.85rem'
                        }}>
                          {formatDollars(ourShare)}
                        </span>
                      </td>
                    </tr>
                  );
                })}

                {/* Totals Row */}
                {completedComparisons.length > 0 && (
                  <tr style={{ borderTop: '2px solid #2c59a2', backgroundColor: '#f0f5ff' }}>
                    <td style={{ padding: '0.85rem 1rem', fontWeight: '700', color: '#1e40af' }}>Total</td>
                    <td style={{ padding: '0.85rem 1rem', textAlign: 'right', fontWeight: '600', color: '#374151' }}>
                      {completedTotals.totalBaselineM3.toLocaleString('en-US', { maximumFractionDigits: 1 })}
                    </td>
                    <td style={{ padding: '0.85rem 1rem', textAlign: 'right', fontWeight: '600', color: '#374151' }}>
                      {completedTotals.totalCurrentM3.toLocaleString('en-US', { maximumFractionDigits: 1 })}
                    </td>
                    <td style={{ padding: '0.85rem 1rem', textAlign: 'right' }}>
                      <span style={{ fontWeight: '700', color: completedTotals.totalSavedM3 >= 0 ? '#059669' : '#dc2626' }}>
                        {completedTotals.totalSavedM3 >= 0 ? '+' : ''}{completedTotals.totalSavedM3.toLocaleString('en-US', { maximumFractionDigits: 1 })} m³
                      </span>
                    </td>
                    <td style={{ padding: '0.85rem 1rem', textAlign: 'right', fontWeight: '700', color: completedTotals.totalSavedDollars > 0 ? '#059669' : '#6b7280' }}>
                      {formatDollars(Math.max(0, completedTotals.totalSavedDollars))}
                    </td>
                    <td style={{ padding: '0.85rem 1rem', textAlign: 'right' }}>
                      <span style={{ backgroundColor: '#e5e7eb', color: '#374151', borderRadius: '6px', padding: '0.3rem 0.75rem', fontWeight: '700', fontSize: '0.9rem' }}>
                        {formatDollars(Math.max(0, completedTotals.yourShare))}
                      </span>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        )}

        {/* ---- Section: Daily Consumption Table (current month only) ---- */}
        {dateFilter === 'currentMonth' && dailyChartData.length > 0 && (
          <div style={{
            backgroundColor: '#fff',
            borderRadius: '12px',
            padding: '1.5rem',
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            marginBottom: '2rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.25rem' }}>
              <FaTable style={{ color: '#2c59a2', fontSize: '1rem' }} />
              <h5 style={{ margin: 0, fontWeight: '600', color: '#1f2937' }}>Daily Consumption — {currentMonthLabel}</h5>
            </div>
            <div className="manager-table-scroll" role="region" aria-label="Water savings details, scroll horizontally for all columns" tabIndex={0} style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                    {['Day', 'Date', 'Usage (m³)', 'Baseline Avg (m³)', 'Difference (m³)'].map((h, i) => (
                      <th key={i} style={{
                        padding: '0.75rem 1rem',
                        textAlign: i === 0 ? 'left' : 'right',
                        color: '#374151',
                        fontWeight: '600',
                        fontSize: '0.85rem'
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {dailyChartData.filter(d => d.actual != null).map((d, idx) => {
                    const diff = d.baselineAvg - d.actual;
                    const isSaved = diff > 0;
                    return (
                      <tr key={idx} style={{
                        borderBottom: '1px solid #f3f4f6',
                        transition: 'background-color 0.15s'
                      }}
                      onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f9fafb'}
                      onMouseLeave={e => e.currentTarget.style.backgroundColor = ''}
                      >
                        <td style={{ padding: '0.75rem 1rem', fontWeight: '500', color: '#1f2937' }}>
                          Day {d.day}
                        </td>
                        <td style={{ padding: '0.75rem 1rem', textAlign: 'right', color: '#6b7280' }}>
                          {d.label}
                        </td>
                        <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontWeight: '600', color: '#1f2937' }}>
                          {d.actual.toFixed(1)}
                        </td>
                        <td style={{ padding: '0.75rem 1rem', textAlign: 'right', color: '#9ca3af' }}>
                          {d.baselineAvg.toFixed(1)}
                        </td>
                        <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontWeight: '600', color: isSaved ? '#059669' : '#dc2626' }}>
                          {isSaved ? '↓ ' : '↑ '}{Math.abs(diff).toFixed(1)}
                        </td>
                      </tr>
                    );
                  })}
                  {/* Total row */}
                  {(() => {
                    const daysWithData = dailyChartData.filter(d => d.actual != null);
                    const totalActual = daysWithData.reduce((sum, d) => sum + d.actual, 0);
                    const totalBaseline = dailyChartData[0]?.baselineAvg * daysWithData.length;
                    const totalSaved = totalBaseline - totalActual;
                    const isTotalSaved = totalSaved > 0;
                    return (
                      <tr style={{ borderTop: '2px solid #2c59a2', backgroundColor: '#f0f5ff' }}>
                        <td colSpan={2} style={{ padding: '0.75rem 1rem', fontWeight: '700', color: '#1e40af' }}>
                          Total ({daysWithData.length} days)
                        </td>
                        <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontWeight: '700', color: '#1f2937' }}>
                          {totalActual.toFixed(1)} m³
                        </td>
                        <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontWeight: '600', color: '#9ca3af' }}>
                          {totalBaseline.toFixed(1)} m³
                        </td>
                        <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontWeight: '700', color: isTotalSaved ? '#059669' : '#dc2626' }}>
                          {isTotalSaved ? '↓ ' : '↑ '}{Math.abs(totalSaved).toFixed(1)} m³ {isTotalSaved ? 'saved' : 'extra'}
                        </td>
                      </tr>
                    );
                  })()}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Footer info */}
        {waterData.lastUpdated && (
          <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: '0.8rem', paddingBottom: '1rem' }}>
            Last updated: {new Date(waterData.lastUpdated).toLocaleString('en-US', {
              month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit'
            })}
            {` · Water rates vary by month (from city bills)`}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ display: 'flex' }}>
      <div
        className="dashboard-sidebar-host"
        onMouseEnter={() => setCollapsed(false)}
        onMouseLeave={() => setCollapsed(true)}
        style={{ height: '100vh' }}
      >
        <Sidebar
          managerName={buildingManagerName}
          activeKey="waterSavings"
          onNavigate={handleNavigate}
          collapsed={collapsed}
          onToggle={() => setCollapsed((c) => !c)}
          buildingPhotoUrl={buildingPhotoUrl}
          buildingName={buildingName}
          showProjectsTab={hasProjectContract}
          showValveAuditTab={hasValveAuditContract}
        />
      </div>

      <div
        className="dashboard-content manager-contract-page"
        style={{
          flex: 1,
          marginLeft: collapsed ? 64 : 220,
          transition: 'margin-left 200ms cubic-bezier(0.4, 0, 0.2, 1)',
          minWidth: 0,
          minHeight: '100vh',
          backgroundColor: '#f8f9fa',
          overflowX: 'hidden'
        }}
      >
        <DashboardNavbar
          logoutInSidebar
          buildingName={buildingName}
          buildingId={buildingId}
          contractLabel="Water Savings"
        />

        <WaterSavingsHeader valveInstallDate={waterData?.valveInstallDate} />

        {renderContent()}
      </div>
    </div>
  );
}
